import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { MarketingTileItem } from './MarketingTiles';
import Button from './Button';
import { renderDescriptionParagraphs } from '@rb-serve-app/core';

interface MarketingTileModalProps {
  isOpen: boolean;
  onClose: () => void;
  tile: MarketingTileItem | null;
  assets?: any;
}

const MarketingTileModal: React.FC<MarketingTileModalProps> = ({
  isOpen,
  onClose,
  tile,
  assets,
}) => {
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);

  // Get contentful data for common strings
  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );
  const { entries } = contentfulData || {};
  const rb_common = entries?.rb_common;

  if (!isOpen || !tile) return null;

  const handleLearnMore = () => {
    if (tile.link) {
      window.open(tile.link, '_blank');
    }
    onClose();
  };

  const toggleDisclaimer = () => {
    setIsDisclaimerOpen(!isDisclaimerOpen);
  };

 
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[15px] w-full max-w-lg md:max-w-2xl lg:max-w-4xl max-h-[85vh] md:max-h-[90vh] overflow-hidden flex flex-col mx-4">
        {/* Top Section - Match the marketing tile layout */}
        <div className="bg-[#1F1F1F] rounded-t-[15px] overflow-hidden">
          <div className="flex h-40 md:h-64 lg:h-80">
            {/* Text Section - Match tile proportions */}
            <div className="w-[58%] md:w-[60%] p-4 md:p-6 lg:p-8 flex flex-col justify-center">
              <h2
                aria-label={tile.title}
                className="text-white text-base md:text-lg lg:text-xl xl:text-2xl font-normal leading-tight"
              >
                {tile.title}
              </h2>
            </div>

            {/* Image Section - Match tile image display exactly */}
            <div className="w-[42%] md:w-[40%]">
              {assets && tile.imageUrl && (
                <img
                  src={assets[tile.imageUrl]}
                  alt={tile.title}
                  className="h-full w-full object-cover"
                />
              )}
            </div>
          </div>
        </div>

        {/* Description Section - Responsive padding */}
        {tile.description && (
          <div
            aria-label={tile.description}
            className="py-4 px-4 md:py-8 md:px-6 lg:px-8 text-[#1F1F1F] overflow-y-auto flex-grow text-sm md:text-base"
          >
            {renderDescriptionParagraphs(tile.description ,"text-[15px] md:text-[15px] text-gray-600 max-w-4xl leading-6")}
          </div>
        )}

        {/* Legal Section - Responsive padding */}
        {tile.disclaimerLabel && (
          <div
            className="py-1 px-4 md:px-6 lg:px-8 text-[#DA271C] overflow-y-auto flex-grow text-sm md:text-base cursor-pointer"
            onClick={toggleDisclaimer}
          >
            [+] {tile.disclaimerLabel}
          </div>
        )}

        {/* Legal Section - Responsive padding */}
        {tile.disclaimerContent && isDisclaimerOpen && (
          <div className="py-4 px-4 md:py-8 md:px-6 lg:px-8 text-[#1F1F1F] overflow-y-auto flex-grow text-sm md:text-base">
            {renderDescriptionParagraphs(tile.disclaimerContent ,"text-[15px] md:text-[15px] text-gray-600 max-w-4xl leading-6")}
          </div>
        )}

        {/* Buttons Section - Stack on mobile, side-by-side on tablet/desktop */}
        <div className="px-4 pb-4 md:px-6 md:pb-6 mt-auto border-t border-gray-100">
          <div
            className={`flex flex-col md:flex-row gap-3 md:gap-4 pt-4 ${!(tile.link && (tile.popupCtaText || tile.ctaText)) ? 'justify-center' : ''}`}
          >
            <Button
              ariaLabel={rb_common?.commonString?.close || 'Close'}
              variant="secondary"
              onClick={onClose}
              className={`${!(tile.link && (tile.popupCtaText || tile.ctaText)) ? 'w-auto md:w-auto px-16 md:px-20 mx-auto h-[52px] md:h-[56px] text-base md:text-lg' : 'w-full md:flex-1 h-[44px] md:h-[48px] text-sm md:text-base'}`}
            >
              {rb_common?.commonString?.close || 'Close'}
            </Button>
            {tile.link && (tile.popupCtaText || tile.ctaText) && (
              <Button
                ariaLabel={tile.popupCtaText || tile.ctaText}
                variant="solid"
                onClick={handleLearnMore}
                className="w-full md:flex-1 h-[44px] md:h-[48px] text-sm md:text-base order-first md:order-last"
                icon={assets?.navigate_next_icon}
                iconPosition="right"
              >
                {tile.popupCtaText || tile.ctaText}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingTileModal;
